import { trackEvent } from '~/features/analytics/track-event';
import type { ExperienceAtfPromoVariant } from '~/features/experience/constants';
import {
  type LaborDay2026SaleVariant,
  getLaborDay2026SaleCheckoutHref,
  LABOR_DAY_2026_SALE_CTA_LABEL,
  LABOR_DAY_2026_SALE_VARIANTS,
} from '~/features/promotions/constants/labor-day-2026-sale';

import type { PromoCampaignSurfaceRenderers } from '../types';

// Bespoke per-campaign components (rather than the shared `SitewideSalePromoCard` /
// `SitewideSaleBanner`) because the Labor Day Figma design (2070:6019 homepage / 2070:6304 catalog)
// ships an orange-to-purple gradient headline (`headlineAccentGradient` via `bgClip='text'`), a solid
// blue CTA, and a purple-bordered catalog banner that the shared components — which are coupled to the
// Summer 2026 single-color headline and outline CTA — do not support.
import { LaborDay2026PromoBanner } from './labor-day-2026-promo-banner';
import { LaborDay2026PromoCard } from './labor-day-2026-promo-card';

const LABOR_DAY_2026_SALE_ANALYTICS_LABEL = 'labor_day_2026_sale_clicked';

/**
 * Both Labor Day market tiers share the same experience ATF visual shell (black card, purple eyebrow,
 * gradient headline, blue CTA). The experience-atf-hero-card branches on this promoVariant for the
 * palette, eyebrow, CTA chrome, and the Labor Day background decoration.
 */
const LABOR_DAY_2026_SALE_ATF_PROMO_VARIANT: Record<LaborDay2026SaleVariant, ExperienceAtfPromoVariant> = {
  labor_day_full: 'labor_day_sale',
  labor_day_low: 'labor_day_sale',
};

export const laborDay2026CampaignSurfaces: PromoCampaignSurfaceRenderers<LaborDay2026SaleVariant>['surfaces'] = {
  homepage: ({ resolved, promoCode, imagePriority }) => (
    <LaborDay2026PromoCard
      imagePriority={imagePriority}
      promoCode={promoCode}
      variant={resolved}
      onClick={() =>
        trackEvent({
          type: 'click',
          name: 'Homepage CTA Clicked',
          label: LABOR_DAY_2026_SALE_ANALYTICS_LABEL,
        })
      }
    />
  ),
  catalog: ({ resolved, promoCode, imagePriority }) => (
    <LaborDay2026PromoBanner
      imagePriority={imagePriority}
      promoCode={promoCode}
      variant={resolved}
      onCtaClick={() =>
        trackEvent({
          type: 'click',
          name: 'Catalog Page CTA Clicked',
          label: LABOR_DAY_2026_SALE_ANALYTICS_LABEL,
        })
      }
    />
  ),
  experience: ({ resolved, promoCode }) => {
    const config = LABOR_DAY_2026_SALE_VARIANTS[resolved];
    const promoVariant = LABOR_DAY_2026_SALE_ATF_PROMO_VARIANT[resolved];

    return {
      id: promoVariant,
      promoVariant,
      badgeImage: config.experienceBadge,
      eyebrow: config.tag,
      title: config.experienceHeadline,
      titleLine2: config.experienceHeadlineLine2,
      description: config.experienceSubtitle,
      ctaLabel: LABOR_DAY_2026_SALE_CTA_LABEL,
      action: {
        type: 'link',
        href: getLaborDay2026SaleCheckoutHref(resolved, promoCode),
      },
    };
  },
};
